import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";

// Nav model: either a flat link, or a group { label, to (optional default), children: [...] }
const navItems = [
  { type: "link", to: "/", label: "Home" },
  {
    type: "group",
    label: "Airport",
    to: "/airport",
    children: [
      { to: "/airport", label: "Airport Info" },
      { to: "/hangars", label: "Hangars" },
      { to: "/fuel", label: "Fuel Prices" },
      { to: "/fees", label: "Layout & Fees" },
      { to: "/reports", label: "Reports" },
    ],
  },
  { type: "link", to: "/gallery", label: "Gallery" },
  { type: "link", to: "/flight-schools", label: "Organizations" },
  { type: "link", to: "/leadership", label: "Team" },
  { type: "link", to: "/attractions", label: "Local" },
  { type: "link", to: "/contact", label: "Contact" },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null); // mobile open group
  const { pathname } = useLocation();
  const onDark = pathname === "/";

  return (
    <header
      data-testid="public-nav"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        onDark ? "bg-slate-950/70 backdrop-blur-xl border-b border-white/10" : "bg-white/85 backdrop-blur-xl border-b border-slate-200"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <Logo size="md" variant={onDark ? "dark" : "auto"} />
          <span className={`hidden sm:inline font-display font-bold tracking-tight text-sm ${onDark ? "text-white" : "text-slate-950"}`}>
            KSME · Lake Cumberland
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) =>
            item.type === "link" ? (
              <DesktopLink key={item.to} item={item} onDark={onDark} />
            ) : (
              <DesktopGroup key={item.label} item={item} onDark={onDark} />
            )
          )}
          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            className={`ml-2 px-3 py-2 text-xs font-mono uppercase tracking-widest border ${
              onDark
                ? "border-white/20 text-white/80 hover:bg-white/10"
                : "border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white"
            } transition-colors`}
          >
            Admin
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className={`lg:hidden p-2 ${onDark ? "text-white" : "text-slate-900"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) =>
              item.type === "link" ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mnav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-2 text-base font-medium ${isActive ? "text-slate-950" : "text-slate-600"}`
                  }
                >
                  {item.label}
                </NavLink>
              ) : (
                <div key={item.label} className="border-b border-slate-100 pb-2 mb-1">
                  <button
                    data-testid={`mnav-group-${item.label.toLowerCase()}`}
                    onClick={() => setOpenGroup(openGroup === item.label ? null : item.label)}
                    className="w-full py-2 text-base font-medium text-slate-700 flex items-center justify-between"
                  >
                    {item.label}
                    <ChevronDown className={`h-4 w-4 transition-transform ${openGroup === item.label ? "rotate-180" : ""}`} />
                  </button>
                  {openGroup === item.label && (
                    <div className="pl-3 pb-2 flex flex-col gap-1">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mnav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={({ isActive }) =>
                            `py-1.5 text-sm ${isActive ? "text-slate-950 font-medium" : "text-slate-600"}`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
            <Link
              to="/admin/login"
              onClick={() => setOpen(false)}
              data-testid="mnav-admin-link"
              className="mt-3 inline-block px-4 py-2 border border-slate-900 text-slate-900 text-xs uppercase tracking-widest font-mono w-fit"
            >
              Admin Login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function DesktopLink({ item, onDark }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `px-2 xl:px-3 py-2 text-[13px] font-medium tracking-tight transition-colors ${
          onDark
            ? isActive ? "text-sky-300" : "text-slate-200 hover:text-white"
            : isActive ? "text-slate-950 underline underline-offset-8 decoration-2 decoration-sky-500" : "text-slate-600 hover:text-slate-950"
        }`
      }
    >
      {item.label}
    </NavLink>
  );
}

function DesktopGroup({ item, onDark }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const childPaths = item.children.map((c) => c.to);
  const isActive = childPaths.includes(pathname) || pathname === item.to;

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={`nav-group-${item.label.toLowerCase()}`}
        onClick={() => setOpen((v) => !v)}
        className={`px-2 xl:px-3 py-2 text-[13px] font-medium tracking-tight transition-colors inline-flex items-center gap-1 ${
          onDark
            ? isActive ? "text-sky-300" : "text-slate-200 hover:text-white"
            : isActive ? "text-slate-950 underline underline-offset-8 decoration-2 decoration-sky-500" : "text-slate-600 hover:text-slate-950"
        }`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
          className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-56 z-50"
        >
          <div className="bg-white border border-slate-200 shadow-lg py-2">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-2 text-sm transition-colors ${
                    isActive ? "bg-slate-50 text-slate-950 font-medium border-l-2 border-sky-500" : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
